// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    isFooter = "isFooter",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.isFooter]: boolean;
        [fields.customCSS]: string;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: ${fontConfig.weight.bold};
        `,
    },
};

export const LinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const [styles] = useState(() => mergeToNew(linkListStyles, extendedStyles));
    const [width, setWidth] = React.useState(0);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
    `;

    const directionStyles = css`
        ${fields.direction === "horizontal"
            ? "display: inline-block; padding: 0 5px;"
            : "display: flex;flex-direction:column;"};
        ${styles.linkWrapper?.css}
    `;

    React.useEffect(() => {
        const updateSize = () => {
            setWidth(window.innerWidth);
        };
        window.addEventListener("resize", updateSize);
        updateSize();
        return () => {
            window.removeEventListener("resize", updateSize);
        };
    }, []);

    if (!fields.isFooter) {
        return (
            <StyledWrapper
                css={css`
                    ${alignmentStyles};
                    ${customCssWrapper?.css};
                `}
            >
                <StyledWrapper {...styles.titleWrapper}>
                    {titleLink?.url ? (
                        <Link
                            href={titleLink.url}
                            css={css`
                                margin-bottom: 5px;
                                font-size: ${fontConfig.fontSize.regular}px;
                                line-height: 28px;
                            `}
                        >
                            {fields.title || titleLink.title}
                        </Link>
                    ) : (
                        <Typography
                            {...styles.title}
                            css={css`
                                margin-bottom: 5px;
                                font-size: ${fontConfig.fontSize.regular}px;
                                line-height: 28px;
                            `}
                        >
                            {fields.title}
                        </Typography>
                    )}
                </StyledWrapper>
                <StyledWrapper css={directionStyles}>
                    {links.map(
                        (link, index) =>
                            link.url && (
                                <Link
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${link.url}${link.title}${index}`}
                                    href={link.url}
                                    css={css`
                                        span {
                                            font-size: ${fontConfig.fontSize.small}px;
                                            line-height: 28px;
                                            margin-right: ${fields.direction === "horizontal" ? "5px" : "0"};
                                        }
                                    `}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                >
                                    {fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            ),
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    return (
        <>
            {width > 768 || width === 0 ? (
                <StyledWrapper
                    css={css`
                        ${alignmentStyles};
                        ${customCssWrapper?.css};
                    `}
                >
                    <StyledWrapper {...styles.titleWrapper}>
                        {titleLink && titleLink.url ? (
                            <Link
                                href={titleLink.url}
                                className="section-title"
                                css={css`
                                    margin-bottom: 5px;
                                    font-size: ${fontConfig.fontSize.regular}px;
                                    line-height: 28px;
                                    color: ${props =>
                                        fields.isFooter ? getColor("common.white") : getColor("text.main")} !important;
                                    span {
                                        color: ${props =>
                                            fields.isFooter
                                                ? getColor("common.white")
                                                : getColor("text.main")} !important;
                                    }
                                `}
                            >
                                {fields.title || titleLink.title}
                            </Link>
                        ) : (
                            <Typography
                                {...styles.title}
                                className="section-title"
                                css={css`
                                    margin-bottom: 5px;
                                    font-size: ${fontConfig.fontSize.regular}px;
                                    line-height: 28px;
                                    color: ${props =>
                                        fields.isFooter ? getColor("common.white") : getColor("text.main")} !important;
                                    span {
                                        color: ${props =>
                                            fields.isFooter
                                                ? getColor("common.white")
                                                : getColor("text.main")} !important;
                                    }
                                `}
                            >
                                {fields.title}
                            </Typography>
                        )}
                    </StyledWrapper>
                    <StyledWrapper css={directionStyles}>
                        {links.map(
                            (link, index) =>
                                link.url && (
                                    <Link
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        href={link.url}
                                        css={css`
                                            color: ${props =>
                                                fields.isFooter
                                                    ? getColor("common.white")
                                                    : getColor("text.main")} !important;
                                            span {
                                                font-size: ${fontConfig.fontSize.small}px;
                                                line-height: 28px;
                                                color: ${props =>
                                                    fields.isFooter
                                                        ? getColor("common.white")
                                                        : getColor("text.main")} !important;
                                            }
                                        `}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    >
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                    </Link>
                                ),
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <Accordion headingLevel={1}>
                    <AccordionSection
                        headerProps={{
                            css: css`
                                background-color: transparent;
                                button {
                                    border: 0;
                                    padding-left: 0 !important;
                                    background: transparent;
                                    border-top: 1px solid ${getColor("common.ui3")};
                                    font-size: ${fontConfig.fontSize.regular}px;
                                    font-weight: ${fontConfig.weight.bold};
                                    letter-spacing: 0.22px;
                                    &:focus {
                                        border: 0;
                                        border-top: 1px solid ${getColor("common.ui3")};
                                        background-color: transparent;
                                        padding-left: 0;
                                    }
                                }
                            `,
                        }}
                        panelProps={{
                            css: css`
                                border: 0;
                                background-color: transparent;
                                margin: 0;
                                padding-left: 0;
                            `,
                        }}
                        toggleIconProps={{
                            color: "common.white",
                        }}
                        title={<Typography color="common.white">{fields.title}</Typography>}
                    >
                        <StyledWrapper css={directionStyles}>
                            {links.map(
                                (link, index) =>
                                    link.url && (
                                        <Link
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            href={link.url}
                                            css={css`
                                                color: ${props =>
                                                    fields.isFooter
                                                        ? getColor("common.white")
                                                        : getColor("text.main")} !important;
                                                span {
                                                    font-size: ${fontConfig.fontSize.small}px;
                                                    line-height: 28px;
                                                    color: ${props =>
                                                        fields.isFooter
                                                            ? getColor("common.white")
                                                            : getColor("text.main")} !important;
                                                }
                                            `}
                                            target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                        >
                                            {fields.links[index].fields.overriddenTitle || link.title}
                                        </Link>
                                    ),
                            )}
                        </StyledWrapper>
                    </AccordionSection>
                </Accordion>
            )}
        </>
    );
};

const defaultCustomCss = `.list-wrapper {

}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: LinkList,
    definition: {
        group: "XNGAGE" as any,
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.isFooter,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;

                    if (overriddenTitle) {
                        return overriddenTitle;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }
                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },

                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
